import { convertUTCDate } from '@repo/common-utils';
import { useWorkingPlatform } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Divider, List, semanticColor, Svg, Table, TableCell, useDialog, useToast } from '@repo/ui';
import { CIPrepareDataForm, usePrepareCiAuthData, AFTER_CI_QUERY_STATE, useCiHistoryController, CI_REQUEST_TYPES } from '@repo/user-sign';
import cn from 'clsx';
import { format } from 'date-fns';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useLazyLoadQuery } from 'react-relay';

import s from './MyPageContent.module.scss';

import { myInfoQuery, myInfoQuery$data } from '@/__generated__/myInfoQuery.graphql';
import { LogoutButton } from '@/components/my/page/logout/LogoutButton';
import { myInfo } from '@/graphql/query/my/my-info';
import { loginTypes } from '@/lib/constants/auth';
import { AUTH_PATHS, COMMON_PATHS, MY_PAGE_PATHS } from '@/lib/constants/paths';
import { usePaymentCard } from '@/lib/hooks/use-payment-card';
import { SnsType } from '@/lib/types/auth';
import { addHyphenForTel } from '@/lib/utils/addHyphenForTel';

export const MyPageContent = () => {
  const { bznavRefund, checkUserLeave } = useLazyLoadQuery<myInfoQuery>(myInfo, {}, { fetchPolicy: 'network-only', networkCacheConfig: { force: true } });

  const user = {
    email: bznavRefund.user.email || '',
    name: bznavRefund.user.name || '',
    phone: addHyphenForTel(bznavRefund.user.phone || ''),
    lastLoginType: (bznavRefund.user.lastLoginType || 'EMAIL') as SnsType,
    createdAt: format(convertUTCDate(bznavRefund.user.createdAt), 'yyyy년 M월 d일')
  };

  return <PageComponent user={user} canLeave={!!checkUserLeave?.canLeave} />;
};

export const MyPageFallback = () => {
  return <PageComponent user={{ name: '', email: '', phone: '', lastLoginType: 'EMAIL', createdAt: '' }} canLeave={false} />;
};

function PageComponent({ user, canLeave }: { user: myInfoQuery$data['bznavRefund']['user']; canLeave: boolean }) {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const { workingPlatform, isPartnerPlatform } = useWorkingPlatform();
  const { ciFormRef, prepareData, prepareCiAuthenticationData } = usePrepareCiAuthData(CI_REQUEST_TYPES.USER_UPDATE, MY_PAGE_PATHS.내정보);
  const { updateEntryHistoryLength } = useCiHistoryController('none');
  const { showDialog } = useDialog();
  const { toast } = useToast();
  const lastLoginType = (workingPlatform === 'toss' ? 'TOSS' : (user.lastLoginType ?? 'EMAIL')) as SnsType;

  const { hasRegisteredCard, cardName, cardLogo, cardNumber, fetchCardData } = usePaymentCard();

  const goMarketingNotificationPage = () => {
    sendClickEvent('my-info_body_benefit-noti');
    router.push(MY_PAGE_PATHS.알림설정);
  };

  const handleClickLeave = () => {
    if (!user.phone) {
      showDialog({
        title: '탈퇴를 진행하려면 본인 확인을 위한 휴대폰번호를 먼저 등록해주세요',
        buttons: {
          primary: {
            text: '등록',
            onClick: () => {
              sendClickEvent('my-info_body_phone-register', { location: 'dialog' });
              updatePhoneNumber();
            }
          },
          secondary: {
            text: '취소'
          }
        }
      });
      return;
    }
    sendClickEvent('my-info_body_delete-account');
    if (canLeave) {
      router.push(AUTH_PATHS.회원탈퇴사유);
    } else {
      router.push(MY_PAGE_PATHS.탈퇴불가);
    }
  };

  const handleCardClick = () => {
    router.push(COMMON_PATHS.환급결제카드입력);
  };

  const updatePhoneNumber = async () => {
    const response = await showDialog({
      title: '새 휴대폰번호로 변경하려면 휴대폰 본인인증이 필요해요.',
      description: user.phone ? `휴대폰 번호는 '${user.phone}'이에요.` : undefined,
      buttons: {
        primary: {
          text: '인증하기'
        },
        secondary: {
          text: '취소'
        }
      }
    });
    if (response?.selected === 'primary') {
      updateEntryHistoryLength();
      prepareCiAuthenticationData();
    }
  };

  useEffect(() => {
    fetchCardData();
  }, [fetchCardData]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (params.get('request_type') === CI_REQUEST_TYPES.USER_UPDATE && params.get('state') === AFTER_CI_QUERY_STATE) {
      const ciResult = params.get('ci_result');
      if (ciResult === 'success') {
        toast({ title: '휴대폰번호를 변경했어요.', variant: 'success' });
      } else if (ciResult === 'failure') {
        toast({ title: '휴대폰번호 변경을 실패했어요.', variant: 'warning' });
      }
    }
  }, [toast]);

  return (
    <>
      <ul className={s.my_list}>
        <Table
          headerCell={<TableCell variant={'header'}>이메일 주소</TableCell>}
          contentCell={<TableCell className={'flex-1 text-right'}>{workingPlatform === 'toss' ? '-' : user.email}</TableCell>}
        />
        <Table
          headerCell={<TableCell variant={'header'}>현재 로그인 방법</TableCell>}
          contentCell={
            <TableCell className={'flex-1 text-right'}>
              <p className={'flex items-center justify-end gap-1'}>
                {lastLoginType !== 'EMAIL' && (
                  <Image src={loginTypes[lastLoginType].icon} alt={`${loginTypes[lastLoginType].name} 로그인`} width={24} height={24} />
                )}
                <span className={`${lastLoginType !== 'EMAIL' && 'text-[15px]'}`}>{loginTypes[lastLoginType].name}</span>
              </p>
            </TableCell>
          }
        />
        <Table headerCell={<TableCell variant={'header'}>이름</TableCell>} contentCell={<TableCell className={'flex-1 text-right'}>{user.name}</TableCell>} />
        <Table
          headerCell={
            <TableCell className={'flex items-center'} variant={'header'}>
              휴대폰번호
            </TableCell>
          }
          contentCell={
            <TableCell className={cn('flex flex-1 justify-end')}>
              {user.phone ? (
                user.phone
              ) : (
                <Table
                  className={'justify-end'}
                  headerCell={
                    <>
                      <TableCell className={'flex flex-col text-right !p-0'} variant={'content'}>
                        <div>미설정</div>
                        <div className={'text-info-foreground-main caption-medium-regular'}>등록이 필요해요</div>
                      </TableCell>
                    </>
                  }
                  contentCell={
                    <TableCell className={'flex items-center !p-0'}>
                      <BoxButton
                        size={'small'}
                        className={'ml-3'}
                        variant={'filled-primary'}
                        onClick={() => {
                          sendClickEvent('my-info_body_phone-register', { location: 'list' });
                          updatePhoneNumber();
                        }}
                      >
                        등록
                      </BoxButton>
                    </TableCell>
                  }
                />
              )}
            </TableCell>
          }
        />
        <Table
          headerCell={<TableCell variant={'header'}>가입 일자</TableCell>}
          contentCell={<TableCell className={'flex-1 text-right'}>{user.createdAt}</TableCell>}
        />
        {hasRegisteredCard && (
          <Table
            headerCell={
              <TableCell className={'flex items-center'} variant={'header'}>
                결제 카드
              </TableCell>
            }
            contentCell={
              <>
                <TableCell className={'flex items-center flex-1 justify-end'}>
                  <div className={'flex gap-tiny-medium text-[15px]'}>
                    <Svg src={cardLogo ?? ''} width={24} height={24} />
                    {cardName}({cardNumber.slice(-4)})
                  </div>
                </TableCell>
                <TableCell>
                  <BoxButton size={'small'} className={'ml-3'} variant={'tonal-secondary'} onClick={handleCardClick}>
                    변경
                  </BoxButton>
                </TableCell>
              </>
            }
          />
        )}
      </ul>

      <Divider size={'large'} />

      <div className={'py-normal-medium px-4.5'}>
        <List
          title={'혜택·이벤트 알림'}
          primaryAction
          onClick={goMarketingNotificationPage}
          supportingElement={<Svg src={'ic_chevron-right.svg'} width={18} height={18} color={semanticColor.neutral.foreground.lowest} />}
        />
        {user.phone && (
          <List
            title={'휴대폰번호 변경'}
            primaryAction
            onClick={() => {
              sendClickEvent('my-info_body_phone-change');
              updatePhoneNumber();
            }}
            supportingElement={<Svg src={'ic_chevron-right.svg'} width={18} height={18} color={semanticColor.neutral.foreground.lowest} />}
          />
        )}
        {/* 로그아웃 */}
        {!isPartnerPlatform && <LogoutButton email={user.email as string} loginType={lastLoginType} />}
        {/* 회원탈퇴 */}
        {workingPlatform !== 'kbank' && <List title={'회원탈퇴'} primaryAction onClick={handleClickLeave} />}
      </div>

      <CIPrepareDataForm ciFormRef={ciFormRef} prepareData={prepareData} />
    </>
  );
}
